/**
 * Payment receipt, generated rather than filled.
 *
 * There is no state form for this and no template to fill, so the page is
 * drawn from scratch. It lists what was collected against the deal and says
 * plainly whether the balance is settled — a customer who has paid in full
 * should leave with something that says so.
 */

import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { usDate } from "./mappings";
import type { FormContext } from "./mappings";

const INK = rgb(0.12, 0.16, 0.20);
const SOFT = rgb(0.40, 0.45, 0.49);
const RULE = rgb(0.85, 0.88, 0.91);
const NAVY = rgb(0.10, 0.16, 0.32);

const usd = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export interface ReceiptPayment {
  paidOn: string;
  amountCents: number;
  method: string;
  reference: string | null;
}

const METHOD_LABEL: Record<string, string> = {
  cash: "Cash", card: "Card", check: "Check", ach: "ACH",
  wire: "Wire", lender_check: "Lender check", other: "Other",
};

export async function buildPaymentReceipt(
  ctx: FormContext,
  payments: ReceiptPayment[],
  totalDueCents: number,
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([612, 792]);
  const reg = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  const { dealer, buyer, vehicle, deal } = ctx;
  const collected = payments.reduce((n, p) => n + p.amountCents, 0);
  const owing = totalDueCents - collected;
  const settled = owing <= 0;

  let y = 742;
  const text = (t: string, x: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x, y, size, font: f, color });
  const right = (t: string, xr: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x: xr - f.widthOfTextAtSize(t, size), y, size, font: f, color });
  const rule = (yy: number, from = 48, to = 564, c = RULE) =>
    page.drawLine({ start: { x: from, y: yy }, end: { x: to, y: yy },
                    thickness: 0.75, color: c });

  // Header
  text(settled ? "PAID IN FULL" : "PAYMENT RECEIPT", 48, 20, bold, settled ? NAVY : INK);
  right(`Receipt for deal ${deal.dealNo}`, 564, 10, reg, SOFT);
  y -= 16;
  text(dealer.dba ?? dealer.legalName, 48, 11, bold);
  right(usDate(new Date().toISOString().slice(0, 10)), 564, 10, reg, SOFT);
  y -= 13;
  text(`${dealer.street}, ${dealer.city}, ${dealer.state} ${dealer.zip}`, 48, 9, reg, SOFT);
  y -= 11;
  text(`Dealer license ${dealer.flhsmvLicenseNo}`, 48, 9, reg, SOFT);
  y -= 18; rule(y); y -= 20;

  // Who and what
  const pair = (label: string, value: string) => {
    text(label, 48, 9, reg, SOFT);
    text(value, 160, 10, reg);
    y -= 16;
  };
  pair("Received from", [buyer.firstName, buyer.middleName, buyer.lastName]
    .filter(Boolean).join(" "));
  pair("Vehicle", `${vehicle.year} ${vehicle.make} ${vehicle.model}`);
  pair("VIN", vehicle.vin);
  y -= 4; rule(y); y -= 22;

  // Payments
  text("PAYMENTS RECEIVED", 48, 9, bold, SOFT);
  y -= 16;
  for (const p of payments) {
    text(usDate(p.paidOn), 48, 10);
    text(METHOD_LABEL[p.method] ?? p.method, 150, 10);
    if (p.reference) text(p.reference, 250, 10, reg, SOFT);
    right(usd(p.amountCents), 564, 10);
    y -= 15;
    rule(y + 5, 48, 564, rgb(0.93, 0.95, 0.96));
  }
  if (payments.length === 0) {
    text("Nothing collected yet.", 48, 10, reg, SOFT);
    y -= 15;
  }

  y -= 10; rule(y); y -= 20;

  const total = (label: string, cents: number, strong = false) => {
    text(label, 48, strong ? 11 : 10, strong ? bold : reg, strong ? INK : SOFT);
    right(usd(cents), 564, strong ? 12 : 10, strong ? bold : reg);
    y -= 18;
  };
  total("Total due on this deal", totalDueCents);
  total("Total collected", collected);
  rule(y + 6);
  y -= 4;
  total(settled ? "Balance — paid in full" : "Balance remaining",
        Math.abs(owing), true);

  if (settled) {
    y -= 12;
    page.drawRectangle({
      x: 48, y: y - 26, width: 516, height: 34,
      color: rgb(0.91, 0.95, 0.92),
    });
    page.drawText("This deal is paid in full. No balance remains.",
      { x: 62, y: y - 14, size: 11, font: bold, color: rgb(0.17, 0.42, 0.28) });
    y -= 46;
  }

  // Signature
  y -= 30; rule(y); y -= 14;
  text("Received by", 48, 9, reg, SOFT);
  rule(y - 16, 120, 330);
  text("Date", 360, 9, reg, SOFT);
  rule(y - 16, 400, 564);

  y = 60;
  text("This receipt records money collected. It is not a bill of sale and does not "
     + "transfer title.", 48, 8, reg, SOFT);

  doc.setTitle(`${deal.dealNo} payment receipt`);
  doc.setProducer("FrontLot");
  return doc.save();
}
